import React, { useEffect, useMemo, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Columns3 } from 'lucide-react';

/**
 * Reusable "Columns" gear menu for tabular pages.
 *
 * Mirrors the Command Center customize-KPIs popover UX so the app has one
 * mental model for "hide things I don't need". Persists hidden-column keys to
 * localStorage under `storageKey` so the preference survives reloads.
 *
 * @param {object[]} columns         { key, label, required? } — required columns are
 *                                   listed but disabled in the checkbox so they
 *                                   can never be hidden (e.g., Name).
 * @param {Set<string>} hiddenKeys   Currently-hidden column keys.
 * @param {function} onChange        (newHiddenSet: Set<string>) => void
 * @param {string} storageKey        localStorage key (e.g., "users-table-cols").
 * @param {string} testid            Optional test id prefix.
 */
export const ColumnsMenu = ({ columns, hiddenKeys, onChange, testid = 'columns-menu' }) => {
  const [open, setOpen] = useState(false);
  const toggle = (key, checked) => {
    const next = new Set(hiddenKeys);
    if (checked) next.delete(key);
    else next.add(key);
    onChange(next);
  };
  const hiddenCount = hiddenKeys.size;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          data-testid={`${testid}-trigger`}
          title="Show / hide columns"
        >
          <Columns3 className="w-4 h-4" />
          Columns
          {hiddenCount > 0 && (
            <span
              className="ml-0.5 text-[10px] font-semibold bg-teal-100 text-teal-800 rounded-full px-1.5 py-0.5"
              data-testid={`${testid}-hidden-count`}
            >
              {hiddenCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-3"
        data-testid={`${testid}-popover`}
      >
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
          Show columns
        </div>
        <div className="space-y-1.5">
          {columns.map((col) => {
            const checked = !hiddenKeys.has(col.key);
            return (
              <label
                key={col.key}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition ${
                  col.required
                    ? 'cursor-default text-slate-500'
                    : 'cursor-pointer hover:bg-slate-50'
                }`}
                data-testid={`${testid}-item-${col.key}`}
              >
                <Checkbox
                  checked={checked}
                  disabled={!!col.required}
                  onCheckedChange={(v) => toggle(col.key, !!v)}
                  data-testid={`${testid}-check-${col.key}`}
                />
                <span className="flex-1 truncate">{col.label}</span>
                {col.required && (
                  <span className="text-[9px] uppercase tracking-wide text-slate-400">
                    required
                  </span>
                )}
              </label>
            );
          })}
        </div>
        {hiddenCount > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onChange(new Set())}
              data-testid={`${testid}-reset`}
            >
              Show all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Hook: load + persist hidden-column keys to localStorage for a given page.
 * Returns [hiddenSet, setHiddenSet].
 */
export const useHiddenColumns = (storageKey, defaults = []) => {
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) {
      // ignore — fall through to defaults
    }
    return new Set(defaults);
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...hidden]));
    } catch (_) {
      // localStorage full / disabled — preference simply won't persist.
    }
  }, [hidden, storageKey]);
  return [hidden, setHidden];
};

/**
 * Helper: check if a column is currently visible. Defined here so tables can
 * one-line their conditional cells: `{isVisible('email') && <TableCell>…</TableCell>}`.
 */
export const useVisibleCheck = (hiddenSet) =>
  useMemo(() => (key) => !hiddenSet.has(key), [hiddenSet]);
